import React, { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { AndroidBiometryStrength, BiometricAuth } from "@aparajita/capacitor-biometric-auth";
import { useAuth } from "../../context/AuthContext";
import { LOGO } from "../../lib/constants";
import { toast } from "sonner";
import { Eye, EyeOff, Fingerprint, Heart, Loader2, Lock, Mail, ShieldCheck, Sparkles } from "lucide-react";

const REMEMBER_KEY = "kelifa_admin_remember";

const loadRemembered = () => {
  try {
    return JSON.parse(localStorage.getItem(REMEMBER_KEY) || "{}");
  } catch {
    return {};
  }
};

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [canBiometric, setCanBiometric] = useState(false);
  const [savedCredentials, setSavedCredentials] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bioLoading, setBioLoading] = useState(false);

  useEffect(() => {
    const saved = loadRemembered();
    if (saved.remember) {
      setEmail(saved.email || "");
      setPassword(saved.password || "");
      setRemember(true);
      setSavedCredentials(saved.email && saved.password ? saved : null);
    }
  }, []);

  useEffect(() => {
    let alive = true;
    const check = async () => {
      if (!Capacitor.isNativePlatform()) return;
      const saved = loadRemembered();
      if (!saved.remember || !saved.email || !saved.password) return;
      try {
        const info = await BiometricAuth.checkBiometry();
        if (alive) setCanBiometric(Boolean(info.isAvailable || info.deviceIsSecure));
      } catch {
        if (alive) setCanBiometric(false);
      }
    };
    check();
    return () => { alive = false; };
  }, []);

  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      if (remember) {
        const saved = { remember: true, email, password };
        localStorage.setItem(REMEMBER_KEY, JSON.stringify(saved));
        setSavedCredentials(saved);
      } else {
        localStorage.removeItem(REMEMBER_KEY);
        setSavedCredentials(null);
        setCanBiometric(false);
      }
      navigate("/admin");
    }
    catch (err) {
      const detail = err.response?.data?.detail;
      if (detail) toast.error(detail);
      else if (err.request && !err.response) toast.error("No hay conexión con el servidor. Revisa internet o la URL del VPS.");
      else toast.error("No pudimos iniciar sesión.");
    }
    setLoading(false);
  };

  const loginWithBiometric = async () => {
    const saved = savedCredentials || loadRemembered();
    if (!saved?.email || !saved?.password) {
      toast.error("Primero inicia sesión con Recordarme activado.");
      return;
    }
    setBioLoading(true);
    try {
      await BiometricAuth.authenticate({
        reason: "Confirma que eres tú para entrar al dashboard de Kelifa.",
        cancelTitle: "Cancelar",
        allowDeviceCredential: true,
        iosFallbackTitle: "Usar código",
        androidTitle: "Entrar a Kelifa",
        androidSubtitle: "Usa tu huella, rostro o bloqueo del teléfono",
        androidConfirmationRequired: false,
        androidBiometryStrength: AndroidBiometryStrength.weak,
      });
      await login(saved.email, saved.password);
      navigate("/admin");
    } catch (err) {
      if (!["userCancel", "systemCancel", "appCancel"].includes(err?.code)) {
        toast.error("No pudimos validar la biometría. Intenta con contraseña.");
      }
    } finally {
      setBioLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#2A0714] px-5 py-8 flex items-center justify-center">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_15%,rgba(255,248,237,0.24),transparent_31%),radial-gradient(circle_at_20%_78%,rgba(220,169,181,0.30),transparent_30%),linear-gradient(155deg,#4B1025_0%,#761B38_34%,#FFF8ED_100%)]" />
      <div className="absolute inset-0 opacity-[0.22] bg-[linear-gradient(rgba(255,255,255,.18)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.16)_1px,transparent_1px)] bg-[size:42px_42px]" />
      <div className="login-blob absolute -top-24 -left-16 w-72 h-72 bg-rosa/45 blur-3xl rounded-full" />
      <div className="login-blob absolute top-20 -right-24 w-80 h-80 bg-dorado/45 blur-3xl rounded-full [animation-delay:-3s]" />
      <div className="login-blob absolute -bottom-28 left-1/4 w-96 h-96 bg-crema/45 blur-3xl rounded-full [animation-delay:-6s]" />

      <div className="pointer-events-none absolute inset-0">
        {[...Array(10)].map((_, i) => (
          <span key={i}
            className="login-float absolute w-2 h-2 rounded-full bg-crema/70 shadow-[0_0_18px_rgba(255,248,237,0.8)]"
            style={{
              left: `${8 + ((i * 19) % 84)}%`,
              top: `${10 + ((i * 23) % 78)}%`,
              animationDelay: `${i * -0.45}s`
            }}
          />
        ))}
      </div>

      <div className="relative z-10 w-full max-w-[420px]">
        <div className="mb-5 text-center text-crema">
          <div className="mx-auto mb-3 inline-flex items-center gap-2 rounded-full border border-crema/20 bg-crema/10 px-4 py-2 text-xs backdrop-blur-xl shadow-[0_10px_40px_rgba(0,0,0,0.18)]">
            <Sparkles className="w-3.5 h-3.5 text-dorado" />
            <span className="tracking-[0.26em] uppercase">Admin Kelifa</span>
          </div>
          <h1 className="brand-title-brasika text-5xl sm:text-6xl leading-none drop-shadow-sm">Kelifa</h1>
          <p className="mt-2 text-sm text-crema/75">Tu repostería, tus pedidos y tu día en un solo lugar.</p>
        </div>

        <form onSubmit={submit} className="login-card-shine relative overflow-hidden bg-blancoCalido/92 backdrop-blur-2xl border border-white/45 rounded-[38px] p-6 sm:p-8 shadow-[0_26px_80px_rgba(47,33,29,0.32)] w-full">
          <div className="absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-white/80 to-transparent" />
          <div className="text-center mb-6">
            <div className="relative mx-auto mb-4 w-24 h-24">
              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-dorado/45 via-rosa/40 to-vino/25 blur-xl" />
              <img src={LOGO} alt="Kelifa" className="relative w-24 h-24 rounded-full mx-auto object-cover border-[6px] border-crema shadow-[0_16px_45px_rgba(118,27,56,0.24)]" />
              <div className="absolute -right-1 -bottom-1 w-9 h-9 rounded-full bg-vino text-crema flex items-center justify-center border-4 border-blancoCalido shadow-lg">
                <Heart className="w-4 h-4 fill-current" />
              </div>
            </div>
            <p className="inline-flex items-center gap-1.5 rounded-full bg-vino/8 px-3 py-1 text-xs text-vino">
              <ShieldCheck className="w-3.5 h-3.5" /> Acceso seguro
            </p>
          </div>

          <label className="text-sm font-medium text-vino mb-1.5 block">Correo</label>
          <div className="relative mb-4">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-rosa" />
            <input data-testid="login-email" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required
              placeholder="admin@kelifa.local"
              className="w-full bg-crema/90 border-2 border-beige rounded-2xl pl-11 pr-4 py-3.5 text-cafe focus:outline-none focus:border-vino shadow-inner" />
          </div>

          <label className="text-sm font-medium text-vino mb-1.5 block">Contraseña</label>
          <div className="relative mb-4">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-rosa" />
            <input data-testid="login-password" type={showPassword ? "text" : "password"} autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required
              placeholder="Tu contraseña"
              className="w-full bg-crema/90 border-2 border-beige rounded-2xl pl-11 pr-12 py-3.5 text-cafe focus:outline-none focus:border-vino shadow-inner" />
            <button type="button" onClick={() => setShowPassword((v) => !v)} aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-full text-vino hover:bg-beige/70">
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>

          <label className="mb-6 flex items-center justify-between gap-3 rounded-2xl border border-beige/80 bg-crema/55 px-4 py-3 text-sm text-cafe/70">
            <span>Recordarme en este dispositivo</span>
            <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)}
              className="h-5 w-5 accent-vino" />
          </label>

          <button data-testid="login-submit" disabled={loading} type="submit"
            className="relative w-full overflow-hidden bg-vino text-crema rounded-full py-4 font-medium hover:bg-vinoDark transition-all disabled:opacity-60 flex items-center justify-center gap-2 shadow-[0_18px_35px_rgba(118,27,56,0.30)] active:scale-[0.99]">
            <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-120%] group-hover:translate-x-[120%]" />
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Lock className="w-4 h-4" />} Entrar al dashboard
          </button>

          {canBiometric && savedCredentials && (
            <button type="button" onClick={loginWithBiometric} disabled={bioLoading}
              className="mt-3 w-full justify-center border-2 border-vino/25 bg-crema/80 text-vino rounded-full px-5 py-3 text-sm font-medium flex items-center gap-2 hover:bg-beige/70 disabled:opacity-60">
              {bioLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Fingerprint className="w-4 h-4" />}
              Entrar con huella / rostro
            </button>
          )}
        </form>
      </div>
    </div>
  );
}
